import type { Commonv1Value } from '@/generated/hooks/grimoire/models';
import { getValueType, type TConfigValueKind } from './typeDetection';
import { toCommonValue, toDisplayValue } from './valueConversion';

/** Kinds `encodeConfigValue` knows how to round-trip through a plain text
 *  editor while preserving the value's original Commonv1Value variant. */
const SCALAR_KINDS = [
  'text',
  'number',
  'boolean',
  'date',
  'datetime',
  'time',
  'file',
] as const;
type TScalarKind = (typeof SCALAR_KINDS)[number];

export { getValueType };
export type { TConfigValueKind };

/** Renders any Commonv1Value as an editable string, regardless of kind. */
export function displayConfigValue(value?: Commonv1Value): string {
  const display = toDisplayValue(value);
  return typeof display === 'boolean' ? String(display) : display;
}

/** Re-encodes an edited value back into the SAME Commonv1Value variant the
 *  field already had (`kind`) — editing a number stays a number, editing a
 *  date stays a date. Shared by the SOP view's per-step config tab and the step
 *  workbench's params rail, so both type config values the same way. `duration` has no toCommonValue branch (it's just a
 *  plain string field); everything else not otherwise recognized (a brand
 *  new list/dictionary/set item, or an unrecognized kind) falls back to text. */
export function encodeConfigValue(
  value: string | boolean,
  kind: TConfigValueKind | undefined,
): Commonv1Value {
  if (kind === 'duration') return { duration: String(value) };
  if ((SCALAR_KINDS as readonly string[]).includes(kind ?? '')) {
    return toCommonValue(value, kind as TScalarKind);
  }
  return { text: String(value) };
}

/**
 * Encode a TEXT edit of a scalar field, with the two guards that stop a bad edit
 * becoming a plausible-looking wrong value rather than a visible error.
 *
 *  - A non-numeric string in a numeric slot stays text. `encodeConfigValue` would
 *    route it to `encodeNumber`, whose `parseFloat` fallback returns the NUMBER
 *    0 — so clearing a numeric tunable would ship `0` to a live run.
 *  - A string in a boolean slot only decodes for the exact literals
 *    `true`/`false`. `True`, `1`, `yes` or a cleared field stay text rather than
 *    resolving to `false`, because silently flipping a `dry_run` off for a run
 *    that issues real book calls is the worst version of a wrong value.
 *
 * Shared by the live editor's commit path and the overlay decoder, so the two
 * can't drift — the guards previously lived only in the rail's own decoder and
 * were lost when the rail started committing typed values directly.
 */
export function encodeScalarTextEdit(
  text: string,
  kind: TConfigValueKind | undefined,
): Commonv1Value {
  if (kind === 'number' && !Number.isFinite(Number(text.trim() || NaN))) {
    return { text };
  }
  if (kind === 'boolean') {
    if (text !== 'true' && text !== 'false') return { text };
    return encodeConfigValue(text === 'true', kind);
  }
  return encodeConfigValue(text, kind);
}

/**
 * Kinds the DRAFT config overlay stores as a bare JSON primitive (a string,
 * number or boolean). These are exactly the kinds that survive a round-trip
 * through `displayConfigValue`/`encodeConfigValue`, and storing them flat keeps
 * overlays written before the params rail was typed readable — a saved date is
 * still the string `"2026-01-15"`, not a nested object.
 */
const OVERLAY_PRIMITIVE_KINDS = new Set<TConfigValueKind>([
  'text',
  'number',
  'boolean',
  'date',
  'datetime',
  'time',
  'file',
  'duration',
]);

/**
 * Encode a config value for the DRAFT config overlay, whose wire type is
 * `google.protobuf.Value` (so: JSON primitives, objects and arrays only —
 * `AutomationServiceTuneStepConfigBodyValues` is `Record<string, unknown>`).
 *
 * Scalars go out as JSON primitives so a tuned number persists as a number and
 * a boolean as a boolean — the whole point of typing the rail, and the source of
 * the bug where a tuned `50` came back as the string `"50"`. Containers (list,
 * dictionary, set, table) have no primitive form, so they go out as the
 * `Commonv1Value` wire object itself, which `overlayJsonToConfigValue` reads
 * straight back. `null` clears the key, per `TuneStepConfig`'s contract.
 */
export function configValueToOverlayJson(value: Commonv1Value): unknown {
  const kind = getValueType(value);
  if (kind === 'null') return null;
  if (kind === 'boolean') return !!value.bool_value;
  if (kind === 'number') {
    const display = displayConfigValue(value);
    const parsed = Number(display);
    return display.trim() !== '' && Number.isFinite(parsed) ? parsed : display;
  }
  if (kind && OVERLAY_PRIMITIVE_KINDS.has(kind))
    return displayConfigValue(value);
  // Containers keep their wire shape; there is no lossless primitive for them.
  return value;
}

/**
 * Decode an overlay entry back into a config value, using the AUTHORED value's
 * kind as the hint — JSON alone can't tell a `date` from a `text`, so without
 * the authored kind every saved date would come back as text and change the
 * value's type behind the user's back.
 *
 * Scalar decoding goes through `encodeScalarTextEdit`, so the numeric and boolean
 * guards are the same ones the live editor's commit path applies.
 */
export function overlayJsonToConfigValue(
  json: unknown,
  authored?: Commonv1Value,
): Commonv1Value {
  if (json === null || json === undefined) return { null_value: 'NULL_VALUE' };
  // A container was stored as its own wire object — hand it straight back.
  // Arrays are excluded on purpose: `typeof [] === 'object'`, and a bare JSON
  // array (an overlay written by another client, or a hand-edited draft) has no
  // `Commonv1Value` variant, so it would come back as a value `getValueType`
  // reads as `undefined` — rendering an empty text box that overwrites the array
  // on first blur. Falling through to the text path at least preserves it.
  if (typeof json === 'object' && !Array.isArray(json)) {
    return json as Commonv1Value;
  }

  const kind = getValueType(authored);
  if (typeof json === 'boolean') return { bool_value: json };
  if (typeof json === 'number') {
    return kind === 'number'
      ? encodeConfigValue(String(json), 'number')
      : { text: String(json) };
  }

  return encodeScalarTextEdit(String(json), kind);
}
